import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Route, Routes } from 'react-router-dom';
import { cssTransition, ToastContainer } from 'react-toastify';
import MilestoneCelebration from './components/gamification/components/MilestoneCelebration';
import TrophyModal from './components/gamification/components/TrophyModal';
import { primeCelebrationSound } from './components/gamification/pointsSettings';
import ProtectedRoute from './ProtectedRoute';
import { installIdleLogout } from './components/helpers/idleLogout';
import { logoutProcessor } from './components/helpers/logoutProcessor';
import { setLogoutReason } from './components/helpers/logoutReason';
import {
    NETWORK_BLOCKED_EVENT, NetworkBlockedDetail, SESSION_EXPIRED_EVENT, SessionExpiredDetail,
} from './api/apiFetch';
import { bootstrapStatusConfig } from './components/lanes/laneStatusConfig';

import './styles/app.css';
import "react-toastify/dist/ReactToastify.css";

import Header from './components/header';
import SideNav from './components/SideNav';
import { SideNavProvider } from './components/SideNavContext';
import { AccessProvider } from './access/AccessContext';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import NetworkStatus from './components/NetworkStatus';
import NetworkBlocked from './components/NetworkBlocked';

import Home from './pages/home';
import AdminAccessControl from './pages/admin-access-control';
import Login from './pages/login';
import LoginLinkApprove from './pages/login-link';
import ChangePassword from './pages/change-password';
import AccountSetup from './pages/account-setup';
import Admin from './pages/admin';
import AdminUsers from './pages/users';
import AdminLogs from './pages/logs';
import AdminDevices from './pages/devices';
import AdminZoneStorage from './pages/zone-storage-settings';
import ScanSheet from './pages/scansheet';
import GoodsInMenu from './pages/goods-in';
import Relabel from './pages/relabel';
import BookIn from './pages/book-in';
import ZoneStorage from './pages/zone-storage';
import Conveyor from './pages/conveyor';
import AdminDisplays from './pages/admin-displays';
import CompleteChutesDisplay from './pages/complete-chutes-display';
import Dispatch from './pages/dispatch';
import DispatchMenu from './pages/dispatch-menu';
import DispatchUnload from './pages/dispatch-unload';
import DispatchLoad from './pages/dispatch-load';
import DispatchFoundItems from './pages/dispatch-found-items';
import AdminOutbound from './pages/admin-outbound';
import PalletDispatch from './pages/pallet-dispatch';
import DispatchFlagged from './pages/dispatch-flagged';
import DispatchFlaggedResolve from './pages/dispatch-flagged-resolve';
import AdminOrders from './pages/admin-orders';
import AdminReturns from './pages/admin-returns';
import AdminPrinters from './pages/admin-printers';
import AdminSettings from './pages/admin-settings';
import AdminBoxes from './pages/admin-boxes';
import AdminProducts from './pages/admin-products';
import AdminSuppliers from './pages/admin-suppliers';
import Warehouse from './pages/warehouse';
import AdminTotes from './pages/admin-totes';
import AdminPutawayQueue from './pages/admin-putaway-queue';
import AdminLocations from './pages/admin-locations';
import AdminLanes from './pages/admin-lanes';
import AdminPicking from './pages/admin-picking';
import AdminDashboard from './pages/admin-dashboard';
import AdminInventory from './pages/admin-inventory';
import AdminInbound from './pages/admin-inbound';
import AdminManifests from './pages/admin-manifests';
import AdminPos from './pages/admin-pos';
import AdminRelabel from './pages/admin-relabel';
import AdminWipe from './pages/admin-wipe';
import AdminManualProducts from './pages/admin-manual-products';
import AdminBundles from './pages/admin-bundles';
import AdminWriteOff from './pages/admin-write-off';
import AdminReports from './pages/admin-reports';
import AdminPallets from './pages/admin-pallets';
import Pallets from './pages/pallets';
import AdminPoints from './pages/admin-points';
import AdminHardware from './pages/admin-hardware';
import PutAway from './pages/put-away';
import Picking from './pages/picking';
import PickingChooseWave from './pages/picking-choose-wave';
import PickFace from './pages/pick-face';
import FlaggedPicks from './pages/flagged-picks';
import ShelfDrop from './pages/shelf-drop';
import ForecastDrop from './pages/forecast-drop';
import PickToTote from './pages/pick-to-tote';
import PalletPicks from './pages/pallet-picks';
import PalletReceiving from './pages/pallet-receiving';
import Wipe from './pages/wipe';
import Move from './pages/move';
import StockControl from './pages/stock-control';
import StockCheck from './pages/stock-check';
import MoveStock from './pages/move-stock';
import SkuCheck from './pages/sku-check';
import SkuFinder from './pages/sku-finder';
import TntOcrTest from './pages/tnt-ocr-test';

// sessionStorage flag set when the operator dismisses the install card.
const PWA_PROMPT_DISMISSED_KEY = 'pwa-prompt-dismissed';

// Apply the cached lane status config (colours, labels, flags, category)
// synchronously and kick off the background BE fetch. Runs at module
// evaluation, before any route renders, so the Lanes module never
// flashes default colours.
bootstrapStatusConfig();

const FastSlide = cssTransition({
    enter: "Toastify__slide-enter",
    exit: "Toastify__slide-exit",
} as any);


function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);
    const deferredPromptRef = useRef<any>(null);
    // Set when the BE's IP allow-list refuses this device (apiFetch raises
    // NETWORK_BLOCKED_EVENT on the first 403 with code "ip_not_allowed").
    // While set, the whole app is replaced by the NetworkBlocked screen.
    const [networkBlocked, setNetworkBlocked] = useState<NetworkBlockedDetail | null>(null);

    // Capture beforeinstallprompt early (fires on page load, Android/Chrome only)
    useEffect(() => {
        const handler = (e: Event) => {
            e.preventDefault();
            deferredPromptRef.current = e;
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    // Idle auto-logout. Basic users 15 min, admins/superAdmins 1 day.
    useEffect(() => {
        return installIdleLogout(() => {
            logoutProcessor().catch(() => {});
            navigate("/login");
        });
    }, [navigate]);

    // Session died server-side. Raised once by apiFetch on the first 401
    // from an authenticated call — most often because the same account
    // signed in on another device, since only one session per user is
    // allowed. Clear local state and bounce to login with a reason so
    // the operator sees why rather than a bare login screen.
    useEffect(() => {
        const handler = (event: Event) => {
            const code = (event as CustomEvent<SessionExpiredDetail>).detail?.code;
            setLogoutReason(code === "session_superseded"
                ? "session_superseded"
                : "session_expired");
            logoutProcessor().catch(() => {});
            navigate("/login", { replace: true });
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, handler);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
    }, [navigate]);

    // This device's network is not on the allow-list. Nothing the user
    // does from here can succeed, so stop rendering pages that would just
    // show failing calls and explain instead. Any number of in-flight
    // requests may raise this at once; the first one wins and the rest
    // are no-ops.
    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<NetworkBlockedDetail>).detail;
            setNetworkBlocked(prev => prev ?? { ip: detail?.ip ?? null, kind: detail?.kind ?? null, path: detail?.path ?? "" });
        };
        window.addEventListener(NETWORK_BLOCKED_EVENT, handler);
        return () => window.removeEventListener(NETWORK_BLOCKED_EVENT, handler);
    }, []);

    // Show install prompt after login (not on login page)
    const isLoggedIn = window.localStorage.getItem("isLoggedIn") === "true";
    // Covers /login, /login/ and the emailed-link confirm page
    // /login/link — all render chromeless like the login screen.
    const isLoginPage = location.pathname.startsWith('/login');

    // Install nudge is a fixed card over the bottom of the screen on phones
    // (<640px). Only offer it on Home: every operator page keeps live
    // controls at the bottom (Book In's Choose Supplier tile, the PO list's
    // Back button, the scan toggles) and the card was sitting on top of
    // them. Dismissal sticks for the session so a reload mid-shift doesn't
    // bring it back.
    const isHomePage = location.pathname === '/';
    useEffect(() => {
        let dismissed = false;
        try { dismissed = window.sessionStorage.getItem(PWA_PROMPT_DISMISSED_KEY) === '1'; } catch { /* ignore */ }
        setShowInstallPrompt(isLoggedIn && isHomePage && !dismissed);
    }, [isLoggedIn, isHomePage]);

    // Warm the warehouse's Points settings so the milestone celebration knows
    // whether it may make noise before the first one fires.
    useEffect(() => {
        if (!isLoggedIn) return;
        try {
            const raw = window.localStorage.getItem("user");
            primeCelebrationSound(raw ? JSON.parse(raw)?.warehouse : undefined);
        } catch {
            // Defaults to audible — a missing setting is not a reason to be silent.
        }
    }, [isLoggedIn]);

    const isDisplayPage = location.pathname === '/complete-chutes';
    const isChangePasswordPage = location.pathname === '/change-password'
                              || location.pathname === '/change-password/';
    const isAccountSetupPage = location.pathname === '/account-setup'
                            || location.pathname === '/account-setup/';
    const showChrome = !isLoginPage && !isDisplayPage && !isChangePasswordPage && !isAccountSetupPage;

    // After every hook above: a blocked network replaces the entire tree.
    // "Try again" is a full reload — the admin has either added the IP
    // (every call now succeeds) or not (this screen comes straight back).
    if (networkBlocked) {
        return <NetworkBlocked ip={networkBlocked.ip} onRetry={() => window.location.reload()} />;
    }

    return (
        <SideNavProvider>
        <AccessProvider>
            <div style={{ height: "100%" }} className="app-shell">
                <NetworkStatus />
                {showChrome && <Header />}
                <div className={`app-body${showChrome ? "" : " app-body--no-chrome"}`}>
                    {showChrome && <SideNav />}
                    <main className="app-main">
                        <Routes>
                <Route path='/' element={<ProtectedRoute level="basic"><Home /></ProtectedRoute>} />
                <Route path='/login' element={<Login />} />
                <Route path='/login/link' element={<LoginLinkApprove />} />
                <Route path='/change-password' element={<ChangePassword />} />
                <Route path='/account-setup' element={<AccountSetup />} />
                <Route path='/admin' element={<ProtectedRoute access="admin" hub><Admin /></ProtectedRoute>} />
                <Route path='/admin/users' element={<ProtectedRoute access="admin.users"><AdminUsers /></ProtectedRoute>} />
                <Route path='/admin/logs' element={<ProtectedRoute access="admin.logs"><AdminLogs /></ProtectedRoute>} />
                <Route path='/admin/devices' element={<ProtectedRoute access="admin.hardware"><AdminDevices /></ProtectedRoute>} />
                <Route path='/admin/zone-storage' element={<ProtectedRoute access="admin.zone_storage"><AdminZoneStorage /></ProtectedRoute>} />
                <Route path='/scansheet' element={<ScanSheet />} />
                <Route path='/goods-in' element={<ProtectedRoute module="goods_in"><GoodsInMenu /></ProtectedRoute>} />
                <Route path='/book-in' element={<ProtectedRoute module="goods_in.book_in"><BookIn /></ProtectedRoute>} />
                <Route path='/zonestorage' element={<ProtectedRoute module="zone_storage"><ZoneStorage /></ProtectedRoute>} />
                <Route path='/conveyor' element={<ProtectedRoute module="conveyor"><Conveyor /></ProtectedRoute>} />
                <Route path='/admin/displays' element={<ProtectedRoute access="admin.displays"><AdminDisplays /></ProtectedRoute>} />
                <Route path='/complete-chutes' element={<ProtectedRoute access="admin.displays"><CompleteChutesDisplay /></ProtectedRoute>} />
                <Route path='/dispatch' element={<ProtectedRoute module="dispatch.start"><Dispatch /></ProtectedRoute>} />
                <Route path='/dispatch/menu' element={<ProtectedRoute module="dispatch"><DispatchMenu /></ProtectedRoute>} />
                <Route path='/dispatch/pallets' element={<ProtectedRoute module="dispatch.pallets"><PalletDispatch /></ProtectedRoute>} />
                <Route path='/dispatch/unload' element={<ProtectedRoute module="dispatch.unload"><DispatchUnload /></ProtectedRoute>} />
                <Route path='/dispatch/load' element={<ProtectedRoute module="dispatch.load"><DispatchLoad /></ProtectedRoute>} />
                <Route path='/dispatch/found-items' element={<ProtectedRoute module="dispatch.found_items"><DispatchFoundItems /></ProtectedRoute>} />
                <Route path='/dispatch/flagged' element={<ProtectedRoute module="dispatch.flagged"><DispatchFlagged /></ProtectedRoute>} />
                <Route path='/dispatch/flagged/:orderId' element={<ProtectedRoute access="dispatch.flagged.dispatch"><DispatchFlaggedResolve /></ProtectedRoute>} />
                <Route path='/admin/warehouse/orders' element={<ProtectedRoute access="admin.orders"><AdminOrders /></ProtectedRoute>} />
                <Route path='/admin/returns' element={<ProtectedRoute access="admin.returns"><AdminReturns /></ProtectedRoute>} />
                <Route path='/admin/printers' element={<ProtectedRoute access="admin.hardware"><AdminPrinters /></ProtectedRoute>} />
                <Route path='/admin/settings' element={<ProtectedRoute access="admin.settings"><AdminSettings /></ProtectedRoute>} />
                <Route path='/admin/access-control' element={<ProtectedRoute access="admin.settings.access_control"><AdminAccessControl /></ProtectedRoute>} />
                <Route path='/admin/boxes' element={<ProtectedRoute access="admin.boxes"><AdminBoxes /></ProtectedRoute>} />
                <Route path='/admin/products' element={<ProtectedRoute access="admin.products"><AdminProducts /></ProtectedRoute>} />
                <Route path='/admin/suppliers' element={<ProtectedRoute access="admin.suppliers"><AdminSuppliers /></ProtectedRoute>} />
                <Route path='/admin/warehouse' element={<ProtectedRoute access="admin.warehouse"><Warehouse /></ProtectedRoute>} />
                <Route path='/admin/hardware' element={<ProtectedRoute access="admin.hardware"><AdminHardware /></ProtectedRoute>} />
                <Route path='/admin/warehouse/totes' element={<ProtectedRoute access="admin.warehouse.totes"><AdminTotes /></ProtectedRoute>} />
                <Route path='/admin/warehouse/putaway-queue' element={<ProtectedRoute access="admin.warehouse.putaway_queue"><AdminPutawayQueue /></ProtectedRoute>} />
                <Route path='/admin/warehouse/locations' element={<ProtectedRoute access="admin.warehouse.locations"><AdminLocations /></ProtectedRoute>} />
                <Route path='/admin/warehouse/lanes' element={<ProtectedRoute access="admin.warehouse.lanes"><AdminLanes /></ProtectedRoute>} />
                <Route path='/admin/picking' element={<ProtectedRoute access="admin.picking"><AdminPicking /></ProtectedRoute>} />
                <Route path='/admin/dashboard' element={<ProtectedRoute access="admin.dashboard"><AdminDashboard /></ProtectedRoute>} />
                <Route path='/admin/warehouse/inventory' element={<ProtectedRoute access="admin.warehouse.inventory"><AdminInventory /></ProtectedRoute>} />
                <Route path='/admin/warehouse/inbound' element={<ProtectedRoute access="admin.warehouse.inbound"><AdminInbound /></ProtectedRoute>} />
                <Route path='/admin/warehouse/manifest' element={<ProtectedRoute access="admin.warehouse.manifest"><AdminManifests /></ProtectedRoute>} />
                <Route path='/admin/warehouse/pos' element={<ProtectedRoute access="admin.warehouse.pos"><AdminPos /></ProtectedRoute>} />
                <Route path='/admin/warehouse/relabel' element={<ProtectedRoute access="admin.warehouse.relabel"><AdminRelabel /></ProtectedRoute>} />
                <Route path='/admin/warehouse/wipe' element={<ProtectedRoute access="admin.warehouse.wipe"><AdminWipe /></ProtectedRoute>} />
                <Route path='/admin/warehouse/manual-products' element={<ProtectedRoute access="admin.warehouse.manual_products"><AdminManualProducts /></ProtectedRoute>} />
                <Route path='/admin/warehouse/bundles' element={<ProtectedRoute access="admin.warehouse.bundles"><AdminBundles /></ProtectedRoute>} />
                <Route path='/admin/warehouse/write-off' element={<ProtectedRoute access="admin.warehouse.write_off"><AdminWriteOff /></ProtectedRoute>} />
                <Route path='/admin/warehouse/reports' element={<ProtectedRoute access="admin.warehouse.reports"><AdminReports /></ProtectedRoute>} />
                <Route path='/admin/warehouse/pallets' element={<ProtectedRoute access="admin.warehouse.pallets"><AdminPallets /></ProtectedRoute>} />
                <Route path='/admin/warehouse/outbound' element={<ProtectedRoute access="admin.warehouse.outbound"><AdminOutbound /></ProtectedRoute>} />
                <Route path='/pallets' element={<ProtectedRoute module="goods_in.pallets"><Pallets /></ProtectedRoute>} />
                <Route path='/admin/warehouse/points' element={<ProtectedRoute access="admin.warehouse.points"><AdminPoints /></ProtectedRoute>} />
                <Route path='/relabel' element={<ProtectedRoute module="goods_in.relabel"><Relabel /></ProtectedRoute>} />
                <Route path='/sku-finder' element={<ProtectedRoute module="goods_in.sku_finder"><SkuFinder /></ProtectedRoute>} />
                <Route path='/put-away' element={<ProtectedRoute module="put_away"><PutAway /></ProtectedRoute>} />
                <Route path='/pick-face' element={<ProtectedRoute module="pick_face"><PickFace /></ProtectedRoute>} />
                <Route path='/pick-face/shelf-drop' element={<ProtectedRoute module="pick_face.shelf_drop"><ShelfDrop /></ProtectedRoute>} />
                <Route path='/pick-face/forecast-drop' element={<ProtectedRoute module="pick_face.forecast_drop"><ForecastDrop /></ProtectedRoute>} />
                <Route path='/pick-face/pick-to-tote' element={<ProtectedRoute module="pick_face.pick_to_tote"><PickToTote /></ProtectedRoute>} />
                <Route path='/pick-face/flagged-picks' element={<ProtectedRoute module="pick_face.flagged_picks"><FlaggedPicks /></ProtectedRoute>} />
                <Route path='/pick-face/pallet-picks' element={<ProtectedRoute module="pick_face.pallet_picks"><PalletPicks /></ProtectedRoute>} />
                <Route path='/picking' element={<ProtectedRoute module="pick_face.pick_queue"><Picking /></ProtectedRoute>} />
                <Route path='/picking/choose' element={<ProtectedRoute module="pick_face.pick_queue"><PickingChooseWave /></ProtectedRoute>} />
                <Route path='/pallet-receiving' element={<ProtectedRoute module="goods_in.inbound"><PalletReceiving /></ProtectedRoute>} />
                <Route path='/wipe' element={<ProtectedRoute module="wipe"><Wipe /></ProtectedRoute>} />
                <Route path='/move' element={<ProtectedRoute module="goods_in.move"><Move /></ProtectedRoute>} />
                <Route path='/stock-control' element={<ProtectedRoute module="stock_control"><StockControl /></ProtectedRoute>} />
                <Route path='/stock-control/stock-check' element={<ProtectedRoute module="stock_control.stock_check"><StockCheck /></ProtectedRoute>} />
                <Route path='/stock-control/move-stock' element={<ProtectedRoute module="stock_control.move_stock"><MoveStock /></ProtectedRoute>} />
                <Route path='/stock-control/sku-check' element={<ProtectedRoute module="stock_control.sku_check"><SkuCheck /></ProtectedRoute>} />
                <Route path='/tnt-ocr-test' element={<TntOcrTest />} />

                <Route path='/' element={<ProtectedRoute level="basic"><Home /></ProtectedRoute>} />
                <Route path='/login/' element={<Login />} />
                <Route path='/login/link/' element={<LoginLinkApprove />} />
                <Route path='/change-password/' element={<ChangePassword />} />
                <Route path='/account-setup/' element={<AccountSetup />} />
                <Route path='/admin/' element={<ProtectedRoute access="admin" hub><Admin /></ProtectedRoute>} />
                <Route path='/admin/users/' element={<ProtectedRoute access="admin.users"><AdminUsers /></ProtectedRoute>} />
                <Route path='/admin/logs/' element={<ProtectedRoute access="admin.logs"><AdminLogs /></ProtectedRoute>} />
                <Route path='/admin/devices/' element={<ProtectedRoute access="admin.hardware"><AdminDevices /></ProtectedRoute>} />
                <Route path='/admin/zone-storage/' element={<ProtectedRoute access="admin.zone_storage"><AdminZoneStorage /></ProtectedRoute>} />
                <Route path='/scansheet/' element={<ScanSheet />} />
                <Route path='/goods-in/' element={<ProtectedRoute module="goods_in"><GoodsInMenu /></ProtectedRoute>} />
                <Route path='/book-in/' element={<ProtectedRoute module="goods_in.book_in"><BookIn /></ProtectedRoute>} />
                <Route path='/zonestorage/' element={<ProtectedRoute module="zone_storage"><ZoneStorage /></ProtectedRoute>} />
                <Route path='/conveyor/' element={<ProtectedRoute module="conveyor"><Conveyor /></ProtectedRoute>} />
                <Route path='/admin/displays/' element={<ProtectedRoute access="admin.displays"><AdminDisplays /></ProtectedRoute>} />
                <Route path='/complete-chutes/' element={<ProtectedRoute access="admin.displays"><CompleteChutesDisplay /></ProtectedRoute>} />
                <Route path='/dispatch/' element={<ProtectedRoute module="dispatch.start"><Dispatch /></ProtectedRoute>} />
                <Route path='/dispatch/menu/' element={<ProtectedRoute module="dispatch"><DispatchMenu /></ProtectedRoute>} />
                <Route path='/dispatch/pallets/' element={<ProtectedRoute module="dispatch.pallets"><PalletDispatch /></ProtectedRoute>} />
                <Route path='/dispatch/unload/' element={<ProtectedRoute module="dispatch.unload"><DispatchUnload /></ProtectedRoute>} />
                <Route path='/dispatch/load/' element={<ProtectedRoute module="dispatch.load"><DispatchLoad /></ProtectedRoute>} />
                <Route path='/dispatch/found-items/' element={<ProtectedRoute module="dispatch.found_items"><DispatchFoundItems /></ProtectedRoute>} />
                <Route path='/dispatch/flagged/' element={<ProtectedRoute module="dispatch.flagged"><DispatchFlagged /></ProtectedRoute>} />
                <Route path='/dispatch/flagged/:orderId/' element={<ProtectedRoute access="dispatch.flagged.dispatch"><DispatchFlaggedResolve /></ProtectedRoute>} />
                <Route path='/admin/warehouse/orders/' element={<ProtectedRoute access="admin.orders"><AdminOrders /></ProtectedRoute>} />
                <Route path='/admin/returns/' element={<ProtectedRoute access="admin.returns"><AdminReturns /></ProtectedRoute>} />
                <Route path='/admin/printers/' element={<ProtectedRoute access="admin.hardware"><AdminPrinters /></ProtectedRoute>} />
                <Route path='/admin/settings/' element={<ProtectedRoute access="admin.settings"><AdminSettings /></ProtectedRoute>} />
                <Route path='/admin/access-control/' element={<ProtectedRoute access="admin.settings.access_control"><AdminAccessControl /></ProtectedRoute>} />
                <Route path='/admin/boxes/' element={<ProtectedRoute access="admin.boxes"><AdminBoxes /></ProtectedRoute>} />
                <Route path='/admin/products/' element={<ProtectedRoute access="admin.products"><AdminProducts /></ProtectedRoute>} />
                <Route path='/admin/suppliers/' element={<ProtectedRoute access="admin.suppliers"><AdminSuppliers /></ProtectedRoute>} />
                <Route path='/admin/warehouse/' element={<ProtectedRoute access="admin.warehouse"><Warehouse /></ProtectedRoute>} />
                <Route path='/admin/hardware/' element={<ProtectedRoute access="admin.hardware"><AdminHardware /></ProtectedRoute>} />
                <Route path='/admin/warehouse/totes/' element={<ProtectedRoute access="admin.warehouse.totes"><AdminTotes /></ProtectedRoute>} />
                <Route path='/admin/warehouse/putaway-queue/' element={<ProtectedRoute access="admin.warehouse.putaway_queue"><AdminPutawayQueue /></ProtectedRoute>} />
                <Route path='/admin/warehouse/locations/' element={<ProtectedRoute access="admin.warehouse.locations"><AdminLocations /></ProtectedRoute>} />
                <Route path='/admin/warehouse/lanes/' element={<ProtectedRoute access="admin.warehouse.lanes"><AdminLanes /></ProtectedRoute>} />
                <Route path='/admin/picking/' element={<ProtectedRoute access="admin.picking"><AdminPicking /></ProtectedRoute>} />
                <Route path='/admin/warehouse/inventory/' element={<ProtectedRoute access="admin.warehouse.inventory"><AdminInventory /></ProtectedRoute>} />
                <Route path='/admin/warehouse/inbound/' element={<ProtectedRoute access="admin.warehouse.inbound"><AdminInbound /></ProtectedRoute>} />
                <Route path='/admin/warehouse/manifest/' element={<ProtectedRoute access="admin.warehouse.manifest"><AdminManifests /></ProtectedRoute>} />
                <Route path='/admin/warehouse/pos/' element={<ProtectedRoute access="admin.warehouse.pos"><AdminPos /></ProtectedRoute>} />
                <Route path='/admin/warehouse/relabel/' element={<ProtectedRoute access="admin.warehouse.relabel"><AdminRelabel /></ProtectedRoute>} />
                <Route path='/admin/warehouse/wipe/' element={<ProtectedRoute access="admin.warehouse.wipe"><AdminWipe /></ProtectedRoute>} />
                <Route path='/admin/warehouse/manual-products/' element={<ProtectedRoute access="admin.warehouse.manual_products"><AdminManualProducts /></ProtectedRoute>} />
                <Route path='/admin/warehouse/bundles/' element={<ProtectedRoute access="admin.warehouse.bundles"><AdminBundles /></ProtectedRoute>} />
                <Route path='/admin/warehouse/write-off/' element={<ProtectedRoute access="admin.warehouse.write_off"><AdminWriteOff /></ProtectedRoute>} />
                <Route path='/admin/warehouse/reports/' element={<ProtectedRoute access="admin.warehouse.reports"><AdminReports /></ProtectedRoute>} />
                <Route path='/admin/warehouse/pallets/' element={<ProtectedRoute access="admin.warehouse.pallets"><AdminPallets /></ProtectedRoute>} />
                <Route path='/admin/warehouse/outbound/' element={<ProtectedRoute access="admin.warehouse.outbound"><AdminOutbound /></ProtectedRoute>} />
                <Route path='/pallets/' element={<ProtectedRoute module="goods_in.pallets"><Pallets /></ProtectedRoute>} />
                <Route path='/admin/warehouse/points/' element={<ProtectedRoute access="admin.warehouse.points"><AdminPoints /></ProtectedRoute>} />
                <Route path='/relabel/' element={<ProtectedRoute module="goods_in.relabel"><Relabel /></ProtectedRoute>} />
                <Route path='/sku-finder/' element={<ProtectedRoute module="goods_in.sku_finder"><SkuFinder /></ProtectedRoute>} />
                <Route path='/put-away/' element={<ProtectedRoute module="put_away"><PutAway /></ProtectedRoute>} />
                <Route path='/pick-face/' element={<ProtectedRoute module="pick_face"><PickFace /></ProtectedRoute>} />
                <Route path='/pick-face/shelf-drop/' element={<ProtectedRoute module="pick_face.shelf_drop"><ShelfDrop /></ProtectedRoute>} />
                <Route path='/pick-face/forecast-drop/' element={<ProtectedRoute module="pick_face.forecast_drop"><ForecastDrop /></ProtectedRoute>} />
                <Route path='/pick-face/pick-to-tote/' element={<ProtectedRoute module="pick_face.pick_to_tote"><PickToTote /></ProtectedRoute>} />
                <Route path='/pick-face/flagged-picks/' element={<ProtectedRoute module="pick_face.flagged_picks"><FlaggedPicks /></ProtectedRoute>} />
                <Route path='/pick-face/pallet-picks/' element={<ProtectedRoute module="pick_face.pallet_picks"><PalletPicks /></ProtectedRoute>} />
                <Route path='/picking/' element={<ProtectedRoute module="pick_face.pick_queue"><Picking /></ProtectedRoute>} />
                <Route path='/picking/choose/' element={<ProtectedRoute module="pick_face.pick_queue"><PickingChooseWave /></ProtectedRoute>} />
                <Route path='/pallet-receiving/' element={<ProtectedRoute module="goods_in.inbound"><PalletReceiving /></ProtectedRoute>} />
                <Route path='/move/' element={<ProtectedRoute module="goods_in.move"><Move /></ProtectedRoute>} />
                <Route path='/stock-control/' element={<ProtectedRoute module="stock_control"><StockControl /></ProtectedRoute>} />
                <Route path='/stock-control/stock-check/' element={<ProtectedRoute module="stock_control.stock_check"><StockCheck /></ProtectedRoute>} />
                <Route path='/stock-control/move-stock/' element={<ProtectedRoute module="stock_control.move_stock"><MoveStock /></ProtectedRoute>} />
                <Route path='/stock-control/sku-check/' element={<ProtectedRoute module="stock_control.sku_check"><SkuCheck /></ProtectedRoute>} />
                <Route path='/tnt-ocr-test/' element={<TntOcrTest />} />
                        </Routes>
                    </main>
                </div>

                {showChrome && <MilestoneCelebration />}
                {showChrome && <TrophyModal />}

                <PWAInstallPrompt
                    show={showInstallPrompt}
                    onDismiss={() => {
                        try { window.sessionStorage.setItem(PWA_PROMPT_DISMISSED_KEY, '1'); } catch { /* ignore */ }
                        setShowInstallPrompt(false);
                    }}
                    deferredPromptRef={deferredPromptRef}
                />

                <ToastContainer
                    position="top-center"
                    autoClose={2500}
                    hideProgressBar={false}
                    newestOnTop={true}
                    closeOnClick
                    draggable={false}
                    pauseOnHover={false}
                    pauseOnFocusLoss={false}
                    theme="colored"
                    transition={FastSlide}
                    limit={1}
                    className="portal-toast-container"
                    toastClassName="portal-toast"
                />
            </div>
        </AccessProvider>
        </SideNavProvider>
    );
}

export default App;
